/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { INavigationMenuViewProps } from '../../../modules/smwe-navigation-menu/./smwe-navigation-menu';
import { IMenuItemData } from '../../../modules/smwe-navigation-menu/./smwe-navigation-menu.data';
import { ISmweNavigationMenuProps } from '../definition-extensions/smwe-navigation-menu.ext.props.autogenerated';

interface INavigationState {
  parentMenu?: number;
  activeMenu?: number;
  mobileViewLabelText?: string;
  navOpen: boolean;
}

interface IMenuItemDataClass extends IMenuItemData {
  className?: string;
}

/**
 *
 * NavigationMenuView component
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<INavigationMenuViewProps & ISmweNavigationMenuProps<{}>, INavigationState> {
  private supportedLevel: number = 3;
  private currentLevel: number = 0;
  private menuNode: React.RefObject<HTMLUListElement>;
  private pathName: string = '';
  private activeClassName: string = 'selected';
  private activeMenuItem: IMenuItemData = {};
  private activeSubMenuItem: IMenuItemData = {};

  constructor(props: INavigationMenuViewProps & ISmweNavigationMenuProps<{}>) {
    super(props);
    this.menuNode = React.createRef();
    this.state = {
      activeMenu: undefined,
      mobileViewLabelText: '',
      parentMenu: undefined,
      navOpen: false
    };
    this._closeSubmenu = this._closeSubmenu.bind(this);
    this._onMobNavOpen = this._onMobNavOpen.bind(this);
    this._onMobNavClose = this._onMobNavClose.bind(this);
  }

  public componentDidMount(): void {
    document.getElementById('ms-header__subnav-fade')?.addEventListener('mousedown', this._handleClickOutside);
  }

  public componentWillUnmount(): void {
    document.getElementById('ms-header__subnav-fade')?.removeEventListener('mousedown', this._handleClickOutside, false);
  }

  public render(): JSX.Element | null {
    const { isMobileView, MenuList, Navigation } = this.props;
    const className = this.props.config.className;
    // get current pathname
    if (process.env.CURRENT_ENVIRONMENT === 'web') {
      this.pathName = window.location.pathname;
    } else {
      this.pathName = this.props.context.request.url.requestUrl.pathname;
    }

    this.props.menuItemData.forEach((item, index) => {
      const ItemPath = this._getPathNameFromURL(item.linkURL || '');
      item.subMenu && item.subMenu.forEach((subItem, subIndex) => {
        const subItemPath = this._getPathNameFromURL(subItem.linkURL || '');
        if (subItemPath === this.pathName) {
          this.activeMenuItem = item;
          this.activeSubMenuItem = subItem;
        }
      });
      if (ItemPath === this.pathName) {
        this.activeMenuItem = item;
      }
    });

    this.currentLevel = 1;
    return (
      <React.Fragment>
        <button
          onClick={this.state.navOpen ? this._onMobNavClose : this._onMobNavOpen}
          className='ms-header__nav-icon'
          data-label={className}
          aria-label='Menu'
          aria-expanded={this.state.navOpen ? 'true' : 'false'}
          aria-haspopup='true'
        />
        <Module {...Navigation} className={classnames(Navigation.className, isMobileView && this.state.activeMenu !== undefined ? 'child' : 'parent', this.state.navOpen ? 'nav-open' : 'nav-closed')}>
          <Node {...MenuList} ref={this.menuNode} role='menubar'>
            {this._renderDisplay()}
          </Node>
        </Module>
        <div id='ms-header__subnav-background' data-expanded={this.state.navOpen ? 'true' : 'false'}/>
        <div id='ms-header__subnav-fade' data-expanded={this.state.navOpen ? 'true' : 'false'}/>
      </React.Fragment>
    );
  }

  private _getPathNameFromURL(linkURL: string): string {
    let pathName = '';
    try {
      // if linkURL is a full url
      pathName = new URL(linkURL).pathname;
    } catch {
      pathName = linkURL.split('?')[0];
    }
    return pathName;
  }

  private _onMobNavOpen(): void {
    const firstNavItem = this.props.menuItemData[0]?.id;

    this.setState({
      activeMenu: firstNavItem || undefined,
      navOpen: true
    });
    this._addFixedBodyPosition();
  }

  private _onMobNavClose(): void {
    this.setState({
      activeMenu: undefined,
      navOpen: false
    });
    this._removeFixedBodyPosition();
  }

  private _renderDisplay(): JSX.Element[] {
    const { ListItem, menuItemData } = this.props;
    const menuItemList: JSX.Element[] = [];
    const isMobileView: boolean = this.props.isMobileView || false;
    const expandOnClick: boolean = this.props.config.expandOnClick || false;

    menuItemData.forEach((item: IMenuItemData, index: number) => {
      menuItemList.push(
        <Node
          {...ListItem}
          key={index}
          onMouseLeave={isMobileView || expandOnClick ? false : this._closeSubmenu}
          role='menu'
        >
          {this._createMenuItemList(item)}
        </Node>
      );
    });

    return menuItemList;
  }

  private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
    if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
      return (
        <React.Fragment>
          {this._renderButtonMenuItem(menuItemData)}
          {this._renderSubMenu(menuItemData.subMenu, menuItemData.id)}
        </React.Fragment>
      );
    } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
      return (this._renderLinkMenuItem(menuItemData));
    } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
      return (this._renderSpanMenuItem(menuItemData));
    }

    return null;
  }

  private _renderSubMenu(subMenus?: IMenuItemDataClass[], activeMenu?: number, openInNewTab?: boolean): JSX.Element | null {
    const { isMobileView, ListItem } = this.props;

    if (!subMenus || subMenus.length === 0) {
      return null;
    }

    let levelClassName: string = '';
    const menuOptions = subMenus && subMenus.map((option: IMenuItemDataClass, idx: number) => {
      const hasOptions = (option.subMenu && option.subMenu.length > 0);
      let menuItem: JSX.Element | null;
      if (hasOptions) {
        menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
      } else {
        menuItem = (option.linkURL
          ? this._renderLinkMenuItem(option, idx, openInNewTab)
          : this._renderSpanMenuItem(option)
        );
      }

      let subMenu;

      if (hasOptions) {
        this.currentLevel++;
        if (this.currentLevel <= this.supportedLevel - 1) {
          levelClassName = `level-${this.currentLevel.toString()}`;
          subMenu = this._renderSubMenu(option.subMenu, isMobileView ? option.id : undefined, openInNewTab);
        }
      }

      return (
        <Node {...ListItem} key={option.id} className={classnames(ListItem.className, option.className)}>
          {menuItem}
          {subMenu}
        </Node>
      );
    });

    return this._renderMenu(levelClassName, menuOptions, activeMenu);
  }

  private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
    const { Button } = this.props;
    const isActive = (option === this.activeMenuItem) || (option === this.activeSubMenuItem);
    const activeClass = `${Button.className} ${isActive ? this.activeClassName : ''}`;
    const isMobileView: boolean = this.props.isMobileView || false;
    const expandOnClick: boolean = this.props.config.expandOnClick || false;

    return (
      <Node
        key={index}
        {...Button}
        className={activeClass}
        onClick={isMobileView || expandOnClick ? this._handleDropdownToggle(option, activeMenu) : () => this._mainNavClick(option)}
        onMouseEnter={isMobileView || expandOnClick ? false : this._handleDropdownToggle(option, activeMenu)}
        aria-haspopup={true}
        aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
        data-parent={activeMenu}
        aria-current={isActive && 'page'}
      >
        {option.linkText}
      </Node>
    );
  }

  private _mainNavClick = (option: IMenuItemData) => {
    const link = option.linkURL;
    if (link) {
      window.location.href = link;
    }
  };

  private _renderLinkMenuItem(option: IMenuItemData, index?: number, openInNewTab?: boolean): JSX.Element | null {
    const { Link } = this.props;
    const isActive = (option === this.activeSubMenuItem) || (option === this.activeMenuItem);

    return (
      <a
        className={classnames(Link.className, isActive ? this.activeClassName : null)}
        key={index}
        href={option.linkURL}
        role='menuitem'
        target={(openInNewTab) ? '_blank' : undefined} rel="noreferrer"
        aria-current={isActive && 'page'}
      >
        {option.linkText}
      </a>
    );
  }

  private _renderSpanMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
    const { Span } = this.props;
    return (
      <Node key={index} {...Span}>{option.linkText}</Node>
    );
  }

  private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number): JSX.Element | null {
    const { MenuList } = this.props;
    this.currentLevel = 1;

    return (
      <Node
        {...MenuList}
        className={classnames(MenuList.className, level)}
        aria-expanded={currentItem && currentItem === this.state.activeMenu ? 'true' : 'false'}
      >
        {menuOptions}
      </Node>
    );
  }

  private _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
    const activeMenu = data.id;
    this.setState({
      activeMenu,
      parentMenu: parentId,
      navOpen: true
    });
    this._addFixedBodyPosition();
    this._resetFocus();
  };

  private _resetFocus = () => {
    if (this.props.isMobileView) {
      setTimeout(() => {
        this.menuNode && this.menuNode.current && this.menuNode.current.focus();
      },         0);
    }
  };

  // tslint:disable-next-line:no-any
  private _handleClickOutside = (event: any) => {
    const burgerMenu: boolean = event.target.className === 'ms-header__nav-icon';

    if (this.menuNode.current && !this.menuNode.current.contains(event.target)) {
      this.setState({ activeMenu: undefined, mobileViewLabelText: '' });

      if (!burgerMenu) {
        this.setState({ navOpen: false });
      }
    }

    this._removeFixedBodyPosition();
    this._resetFocus();
  };

  private _closeSubmenu(): void {
    this.setState({
      activeMenu: undefined,
      mobileViewLabelText: '',
      navOpen: false
    });
    this._removeFixedBodyPosition();
    this._resetFocus();
  }

  private _addFixedBodyPosition(): void {
    document.body?.classList.add('fixed');
  }

  private _removeFixedBodyPosition(): void {
    document.body?.classList.remove('fixed');
  }
}

export default NavigationMenuView;
